<template>
    <page-title
        title="学期評価"
        icon="bi-bricks"
        :title_sub_1="is_muji ? '無地' : ''"
        :backTo="{ name: is_muji ? 'PageRepocraftMujiHome' : 'PageRepocraftHome' }"
    >
    </page-title>

    <inline-loader v-if="loading"></inline-loader>

    <div v-else>
        <summary-box title="対象">
            <summary-box-row
                title=""
                :text="`${Grade.getWhereSchoolType(school.school_type, school_class.grade)} ${school_class.class_name_display}（${school_class.year}年度）`"
            ></summary-box-row>
        </summary-box>

        <!-- 学期ボタン -->
        <div class="nav nav-pills flex-column flex-sm-row border-bottom border-light">
            <template v-for="term in terms" :key="term.key">
                <div class="text-sm-center nav-link text-primary border border-bottom-0 rounded-0 rounded-top cursor mx-2"
                    :class="{'bg-primary text-white active': parseInt(term_id, 10) === parseInt(term.key, 10)}"
                    @click="getEvaluationScore(term.key)"
                >
                    <div class="px-4">{{ term.label }}</div>
                </div>
            </template>
        </div>

        <inline-loader v-if="term_loading"></inline-loader>
        <template v-else>
            <!-- 選択学期 -->
            <summary-box-section title="学期" class="my-3">
                <summary-box-row title="" :text="Term.get(term_id)"></summary-box-row>
            </summary-box-section>

            <!-- 登録題材 -->
            <summary-box-section title="題材" class="my-3">
                <summary-box-row
                    optional_text_class="d-flex"
                >
                    <!-- 題材が存在する場合 -->
                    <template v-if="subjects_in_term.length">
                        <template v-for="subject in subjects_in_term" :key="subject.planned_subject_id">
                            {{subject.subject_id}}
                            <router-link :to="{name: is_muji ? 'PageRepocraftMujiSubjectEvaluation' : 'PageRepocraftSubjectEvaluation', params: {year: parseInt(this.$route.params.year, 10), grade: parseInt(this.$route.params.grade, 10), class: parseInt(this.$route.params.class, 10)}, query: {subject: subject.planned_subject_id}}" class="btn btn-outline-primary me-2">
                                {{ subject.subject_name }}
                                <i class="bi bi-chevron-right"></i>
                            </router-link>
                        </template>
                    </template>
                    <!-- 登録題材 nodata -->
                    <alert-no-record v-else></alert-no-record>
                </summary-box-row>
            </summary-box-section>

            <inline-loader v-if="main_loading"></inline-loader>
            <template v-else>
                <!-- ランクマスタ設定済みの場合 -->
                <form @submit.prevent="save" v-if="detailed_ranks.length && evaluation_ranks.length">
                    <text-warn v-if="edited === true">変更が保存されていません。</text-warn>

                    <button-wrap>
                        <template v-slot:negative>
                            <button-exec-view @click="$refs.confirm_calculate.show();" text="評価自動計算" icon="bi-calculator" size="btn-md"></button-exec-view>
                            <button-exec-view @click="download" text="CSVダウンロード" icon="bi-download" size="btn-md"></button-exec-view>
                        </template>
                    </button-wrap>

                    <table-normal class="mt-3">
                        <colgroup>
                            <col class="col-1">
                            <col class="col-3">
                            <col class="col-1">
                            <col class="col-2">
                            <col class="col-1">
                            <col class="col-2">
                            <col class="col-1">
                            <col class="col-2">
                            <col class="col-1">
                            <col class="col-2">
                            <col class="col-2">
                        </colgroup>
                        <thead>
                            <tr>
                                <th rowspan="3"></th>
                                <th rowspan="3">{{ $student_label() }}</th>
                                <th colspan="6">観点別評価</th>
                                <th colspan="2" rowspan="3">学期評価</th>
                                <th rowspan="3">{{ $student_label() }}への<br>コメント</th>
                            </tr>
                            <tr>
                                <th colspan="2">知識・技能</th>
                                <th colspan="2">思考・判断・表現</th>
                                <th colspan="2">主体的な学習態度</th>
                            </tr>
                            <tr>
                                <th></th>
                                <th>評価</th>
                                <th></th>
                                <th>評価</th>
                                <th></th>
                                <th>評価</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="record in evaluation_records" :key="record.account_user.account_user_id">
                                <td class="text-center">{{ record.account_user.student_number }}</td>
                                <td>{{ record.account_user.student_name }}</td>
                                <td class="text-end" :class="{'disabled' : isAllSubjectsHaveNoEvaluation}">{{ record.average.average_score_1 }}</td>
                                <td class="text-center" :class="{'disabled' : isAllSubjectsHaveNoEvaluation}">
                                    <form-select
                                        v-model="record.evaluation.detailed_rank_id_1"
                                        option_key="detailed_rank_id"
                                        option_label="rank_label"
                                        :options="detailed_ranks"
                                        :disabled="isAllSubjectsHaveNoEvaluation"
                                        empty_option="---"
                                        @change="edited = true"
                                    ></form-select>
                                </td>
                                <td class="text-end" :class="{'disabled' : isAllSubjectsHaveNoEvaluation}">{{ record.average.average_score_2 }}</td>
                                <td class="text-center" :class="{'disabled' : isAllSubjectsHaveNoEvaluation}">
                                    <form-select
                                        v-model="record.evaluation.detailed_rank_id_2"
                                        option_key="detailed_rank_id"
                                        option_label="rank_label"
                                        :options="detailed_ranks"
                                        :disabled="isAllSubjectsHaveNoEvaluation"
                                        empty_option="---"
                                    ></form-select>
                                </td>
                                <td class="text-end" :class="{'disabled' : isAllSubjectsHaveNoEvaluation}">{{ record.average.average_score_3 }}</td>
                                <td class="text-center" :class="{'disabled' : isAllSubjectsHaveNoEvaluation}">
                                    <form-select
                                        v-model="record.evaluation.detailed_rank_id_3"
                                        option_key="detailed_rank_id"
                                        option_label="rank_label"
                                        :options="detailed_ranks"
                                        :disabled="isAllSubjectsHaveNoEvaluation"
                                        empty_option="---"
                                        @change="edited = true"
                                    ></form-select>
                                </td>
                                <td class="text-end">{{ record.average.average_all_score }}</td>
                                <td class="text-center">
                                    <form-select
                                        v-model="record.evaluation.evaluation_rank_id"
                                        option_key="evaluation_rank_id"
                                        option_label="rank_label"
                                        :options="evaluation_ranks"
                                        empty_option="---"
                                        @change="edited = true"
                                    ></form-select>
                                </td>
                                <td>
                                    <button-go-edit @click="modal_record = record; modal_show = true;" text="記入済" icon="bi-pencil-square" class="btn-outline-secondary" v-if="record.evaluation.feedback_comment"></button-go-edit>
                                    <button-go-edit @click="modal_record = record; modal_show = true;" text="未記入" icon="bi-pencil-fill" v-else></button-go-edit>
                                </td>
                            </tr>
                        </tbody>
                    </table-normal>

                    <button-wrap optional_class="mt-5 mb-5">
                        <template v-slot:default>
                            <button-exec-edit></button-exec-edit>
                        </template>
                    </button-wrap>
                </form>
                <!-- ランクマスタ 未設定の場合は -->
                <template  v-else>
                    <div class="row mt-3">
                        <div class="col-16"><alert-no-record :text="ranks_massage"></alert-no-record></div>
                        <div class="col d-flex align-items-center pb-3">
                            <button-go-edit
                                v-if="ranks_massage.length"
                                text="評価ランク設定"
                                :to="{name: is_muji ? 'PageRepocraftMujiSettingReportCard' : 'PageRepocraftSettingReportCard'}"
                                @change="edited = true"
                            ></button-go-edit>
                        </div>
                    </div>
                </template>
            </template>
        </template>
    </div>

    <confirm-dialog ref="confirm_calculate" @ok="calculate">
        <p>評価を自動計算し、入力済の評価を上書きします。よろしいですか？</p>
    </confirm-dialog>

    <div v-if="modal_show">
        <transition name="fade">
            <div class="modal_inner">
                <div class="modal-backdrop show"></div>
                <div class="modal" tabindex="-1" style="display:block">
                    <div class="modal-dialog">
                        <div class="modal-content">
                            <div class="modal-header">
                                <h4 class="modal-title"><i class="bi bi-pencil-fill"></i> {{ modal_record.account_user.student_name }} さんへのコメント</h4>
                                <button type="button" class="btn-close" @click="modal_show = false"></button>
                            </div>
                            <div class="modal-body">
                                <textarea class="form-control" rows="10" v-model="modal_record.evaluation.feedback_comment"></textarea>

                                <button-wrap optional_class="mt-5">
                                    <template v-slot:default>
                                        <button-exec-edit @click="modal_show = false" text="完了"></button-exec-edit>
                                    </template>
                                </button-wrap>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>

</template>

<script>
import { downloadCsv } from '@/shared/utilities/download';
import School from '@/shared/models/entities/school';
import SchoolClass from '@/shared/models/entities/school-class';
import PlannedSubject from '@/shared/models/entities/repocraft/planned-subject';
import DetailedRank from '@/shared/models/entities/repocraft/detailed-rank';
import EvaluationRank from '@/shared/models/entities/repocraft/evaluation-rank';
import Grade from '@/shared/models/enums/grade'
import Term from '@/shared/models/enums/term'
import PageTitle from '@/shared/components/design/PageTitle';
import AlertNoRecord from '@/shared/components/design/AlertNoRecord';
import ButtonWrap from '@/shared/components/design/buttons/ButtonWrap.vue';
import ButtonGoEdit from '@/shared/components/design/buttons/ButtonGoEdit.vue';
import ButtonExecEdit from '@/shared/components/design/buttons/ButtonExecEdit';
import ButtonExecView from '@/shared/components/design/buttons/ButtonExecView';
import FormSelect from '@/shared/components/form/FormSelect.vue';
import SummaryBox from '@/shared/components/design/SummaryBox';
import SummaryBoxSection from '@/shared/components/design/SummaryBoxSection';
import SummaryBoxRow from '@/shared/components/design/SummaryBoxRow';
import TextWarn from '@/shared/components/design/TextWarn';
import TableNormal from '@/shared/components/design/TableNormal.vue';
import ConfirmDialog from '@/shared/components/tool/ConfirmDialog';
import InlineLoader from '@/shared/components/tool/InlineLoader';


export default {
    name: 'PageRepocraftTermEvaluation',
    components: {
        PageTitle,
        AlertNoRecord,
        ButtonWrap,
        ButtonGoEdit,
        ButtonExecEdit,
        ButtonExecView,
        FormSelect,
        SummaryBox,
        SummaryBoxSection,
        SummaryBoxRow,
        TextWarn,
        TableNormal,
        InlineLoader,
        ConfirmDialog,
    },
    inject: [
        'startScreenLoading',
        'endScreenLoading',
        'showMessage',
        'showErrorMessage',
    ],
    data() {
        return {
            is_muji: this.$route.meta?.is_muji ?? false, // レポクラフト無地判定
            app_key: this.$route.meta?.is_muji ? 'repocraft_muji' : 'repocraft', //アプリ名取得

            //ログイン先生の学校情報
            school: new School(this.$store.state.school.auth.school),

            school_class: new SchoolClass(),
            term_id: null,
            subjects: [],

            //メインデータ
            evaluation_records: [],

            //変更フラグ
            edited: false,

            //ランクマスタ系
            detailed_ranks: [],
            evaluation_ranks: [],
            ranks_massage: "",

            //nabs
            terms: Term.optionsFor(this.$store.state.school.auth.school.term_pattern),

            //entity
            Grade,
            Term,

            //loading系
            loading: 1,
            term_loading: 1,
            main_loading: 1,

            //modal
            modal_show: false,
            modal_record: null,
        }
    },
    mounted() {
        this.term_id = parseInt(this.$route.params.term, 10);
        this.getTheClass(parseInt(this.$route.params.year, 10), parseInt(this.$route.params.grade, 10), parseInt(this.$route.params.class, 10));
        this.getDetailedRanks(parseInt(this.$route.params.year, 10), parseInt(this.$route.params.grade, 10));
        this.getEvaluationRanks(parseInt(this.$route.params.year, 10), parseInt(this.$route.params.grade, 10));
    },
    methods: {
        //特定クラス情報の取得 => this.school_class にセット
        getTheClass(year, grade, class_id) {
            this.$http.get(`school/${this.app_key}/year/${year}/grade/${grade}/class/${class_id}`)
            .then(response => {
                this.school_class = new SchoolClass(response.data);

                this.getSubjectList(year, grade);
                this.getEvaluationScore(this.term_id);
            })
            .finally(() => {
                this.loading--;
                this.term_loading--;
                this.main_loading--;
            });
        },
        //学年の題材リストの取得 => this.subjects にセット
        getSubjectList(year, grade) {
            this.term_loading++;
            this.$http.get(`school/${this.app_key}/year/${year}/grade/${grade}/subject`)
            .then(response => {
                for (let subject of response.data) {
                    this.subjects.push(new PlannedSubject(subject));
                }
            })
            .finally(() => {
                this.term_loading--;
            });
        },
        //観点別評価ランクマスタの取得
        getDetailedRanks(year, grade) {
            this.$http.get(`school/${this.app_key}/year/${year}/grade/${grade}/detailed_rank`)
            .then(response => {
                for (let detailed_rank of response.data) {
                    this.detailed_ranks.push(new DetailedRank(detailed_rank));
                }
                this.detailed_ranks.sort((a, b) => a.threshold - b.threshold);
            })
            .finally(() => {
                if (this.detailed_ranks.length === 0) {
                    this.ranks_massage += '観点別評価ランクが設定されていません。';
                }
            });
        },
        //総合評価ランクマスタの取得
        getEvaluationRanks(year, grade) {
            this.$http.get(`school/${this.app_key}/year/${year}/grade/${grade}/evaluation_rank`)
            .then(response => {
                for (let evaluation_rank of response.data) {
                    this.evaluation_ranks.push(new EvaluationRank(evaluation_rank));
                }
                this.evaluation_ranks.sort((a, b) => a.threshold - b.threshold);
            })
            .finally(() => {
                if (this.evaluation_ranks.length === 0) {
                    this.ranks_massage += '総合評価ランクが設定されていません。';
                }
            });
        },
        //評価の取得
        getEvaluationScore(term_id) {
            this.term_id = term_id;
            this.main_loading++;
            //初期化
            this.evaluation_records.splice(0);

            this.$http.get(`school/${this.app_key}/evaluation/term/${term_id}/class/${parseInt(this.$route.params.class, 10)}/score`)
            .then(response => {
                for (let row of response.data) {
                    this.evaluation_records.push({
                        account_user: row.account_user,
                        average: row.average,
                        evaluation: row.evaluation,
                    });
                }
            })
            .finally(() => {
                this.main_loading--;
            });
        },
        calculate() {
            for (let i = 0; i < this.evaluation_records.length; i++) {
                this.evaluation_records[i].evaluation.detailed_rank_id_1 = this.findDetailedRank(this.evaluation_records[i].average.average_score_1);
                this.evaluation_records[i].evaluation.detailed_rank_id_2 = this.findDetailedRank(this.evaluation_records[i].average.average_score_2);
                this.evaluation_records[i].evaluation.detailed_rank_id_3 = this.findDetailedRank(this.evaluation_records[i].average.average_score_3);
                this.evaluation_records[i].evaluation.evaluation_rank_id = this.findEvaluationRank(this.evaluation_records[i].average.average_all_score);
            }
        },
        findDetailedRank(score) {
            if (score === null) {
                return null;
            }

            let rank = this.detailed_ranks[0];
            for (let i = 0; i < this.detailed_ranks.length; i++) {
                if (parseFloat(score) < parseFloat(this.detailed_ranks[i].threshold)) {
                    return rank.detailed_rank_id;
                }
                rank = this.detailed_ranks[i];
            }
            return rank.detailed_rank_id;
        },
        findEvaluationRank(score) {
            if (score === null) {
                return null;
            }
            let rank = this.evaluation_ranks[0];
            for (let i = 0; i < this.evaluation_ranks.length; i++) {
                if (parseFloat(score) < parseFloat(this.evaluation_ranks[i].threshold)) {
                    return rank.evaluation_rank_id;
                }
                rank = this.evaluation_ranks[i];
            }
            return rank.evaluation_rank_id;
        },
        save() {
            this.startScreenLoading();
            this.$http.put(
                `school/${this.app_key}/evaluation/term/${this.term_id}/class/${parseInt(this.$route.params.class, 10)}/score`,
                {evaluations: this.evaluation_records}
            )
            .then(() => {
                this.edited = false;
                this.showMessage('保存しました');
            })
            .finally(() => {
                this.endScreenLoading();
            });
        },
        download() {
            this.startScreenLoading();

            let detailed_ranks_map = {};
            for (let detailed_rank of this.detailed_ranks) {
                detailed_ranks_map[detailed_rank.detailed_rank_id] = detailed_rank.rank_label;
            }
            let evaluation_ranks_map = {};
            for (let evaluation_rank of this.evaluation_ranks) {
                evaluation_ranks_map[evaluation_rank.evaluation_rank_id] = evaluation_rank.rank_label;
            }

            let header = [
                'No',
                '氏名',
                '知識・技能_平均',
                '知識・技能_評価',
                '思考・判断・表現_平均',
                '思考・判断・表現_評価',
                '主体的に学習に取組む態度_平均',
                '主体的に学習に取組む態度_評価',
                '学期評価_平均',
                '学期評価_評価',
            ];
            let records = this.evaluation_records.map((record) => {
                return [
                    record.account_user.student_number,
                    record.account_user.student_name,
                    record.average.average_score_1,
                    detailed_ranks_map[record.evaluation.detailed_rank_id_1] ?? '',
                    record.average.average_score_2,
                    detailed_ranks_map[record.evaluation.detailed_rank_id_2] ?? '',
                    record.average.average_score_3,
                    detailed_ranks_map[record.evaluation.detailed_rank_id_3] ?? '',
                    record.average.average_all_score,
                    evaluation_ranks_map[record.evaluation.evaluation_rank_id] ?? '',
                ];
            });
            downloadCsv(header, records, 'evaluation.csv');
            this.endScreenLoading();
        }
    },
    computed: {
        subjects_in_term() {
            return this.subjects.filter(item => item.term === Number(this.term_id));
        },
        isAllSubjectsHaveNoEvaluation() {
            // 評価項目をもっている題材が一つでもあればfalse
            if (this.subjects.some((subject) => subject.has_any_evaluation)) {
                return false;
            }

            return true;
        },
    },
}
</script>

<style scoped>
    .disabled {background-color: #ececec !important;}
</style>
<style>
    .title-sub-1 {border-color: #198754 !important;}
</style>
